import React, {
  useCallback,
  useMemo,
  useRef,
  useEffect,
  useState,
} from "react";
import { Modal } from "antd";
import { GoogleMap, useJsApiLoader, Marker } from "@react-google-maps/api";
import "./AgentLocationMapModal.scss";

interface LocationPoint {
  lat: number;
  lng: number;
  timestamp: string;
}

interface AgentLocationMapModalProps {
  open: boolean;
  onCancel: () => void;
  agentName: string;
  locations: LocationPoint[];
}

/**
 * Creates or updates a polyline on the map based on the provided locations.
 * Sorts locations by timestamp and draws a path connecting them.
 */
const createOrUpdatePolyline = (
  mapInstance: google.maps.Map,
  locations: LocationPoint[],
  polylineRef: React.MutableRefObject<google.maps.Polyline | null>
): void => {
  if (locations.length < 2) {
    return;
  }

  // Sort locations by timestamp
  const sortedLocations = [...locations].sort(
    (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
  );

  // Remove existing polyline if any
  if (polylineRef.current) {
    polylineRef.current.setMap(null);
    polylineRef.current = null;
  }

  // Create path array in simple format { lat, lng }
  const path = sortedLocations.map((loc) => ({
    lat: loc.lat,
    lng: loc.lng,
  }));

  // Create polyline using the simpler approach
  const polyline = new google.maps.Polyline({
    path: path,
    geodesic: true,
    strokeColor: "#4526a0",
    strokeOpacity: 1.0,
    strokeWeight: 6,
  });

  // Set map after creation
  polyline.setMap(mapInstance);

  polylineRef.current = polyline;
};

const libraries: "places"[] = ["places"];

const AgentLocationMapModal: React.FC<AgentLocationMapModalProps> = ({
  open,
  onCancel,
  agentName,
  locations,
}) => {
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_API_KEY as string,
    libraries: libraries,
  });

  const mapRef = useRef<google.maps.Map | null>(null);
  const polylineRef = useRef<google.maps.Polyline | null>(null);
  const [mapReady, setMapReady] = useState(false);

  const onLoad = useCallback(
    (mapInstance: google.maps.Map) => {
      mapRef.current = mapInstance;
      setMapReady(true);

      if (locations.length > 0) {
        const bounds = new google.maps.LatLngBounds();
        locations.forEach((loc) => {
          bounds.extend({ lat: loc.lat, lng: loc.lng });
        });

        // Add padding to the bounds
        const padding = 50;
        mapInstance.fitBounds(bounds, padding);

        // Create polyline after map is loaded
        createOrUpdatePolyline(mapInstance, locations, polylineRef);
      }
    },
    [locations]
  );

  // Reset when modal closes
  useEffect(() => {
    if (!open) {
      setMapReady(false);
      if (polylineRef.current) {
        polylineRef.current.setMap(null);
        polylineRef.current = null;
      }
      mapRef.current = null;
    }
  }, [open]);

  // Update polyline when locations change (after map is ready)
  useEffect(() => {
    if (!mapRef.current || !isLoaded || !mapReady || locations.length < 2) {
      return;
    }

    createOrUpdatePolyline(mapRef.current, locations, polylineRef);

    return () => {
      if (polylineRef.current) {
        polylineRef.current.setMap(null);
        polylineRef.current = null;
      }
    };
  }, [isLoaded, mapReady, locations]);

  // Calculate center for initial map view
  const center = useMemo(() => {
    if (locations.length === 0) {
      return { lat: 20.5937, lng: 78.9629 }; // Default India center
    }
    const avgLat =
      locations.reduce((sum, loc) => sum + loc.lat, 0) / locations.length;
    const avgLng =
      locations.reduce((sum, loc) => sum + loc.lng, 0) / locations.length;
    return { lat: avgLat, lng: avgLng };
  }, [locations]);

  const formatTimestamp = (timestamp: string): string => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      footer={null}
      width={800}
      title={`${agentName} - Location History`}
      className="agent-location-map-modal"
    >
      {loadError && (
        <div className="error-message">
          Error loading Google Maps. Please refresh the page.
        </div>
      )}
      {!isLoaded && !loadError && (
        <div className="loading-message">Loading map...</div>
      )}
      {isLoaded && locations.length > 0 && (
        <div className="map-container">
          <GoogleMap
            mapContainerStyle={{
              width: "100%",
              height: "500px",
              minHeight: "500px",
            }}
            center={center}
            zoom={locations.length === 1 ? 15 : undefined}
            onLoad={onLoad}
            options={{
              mapTypeControl: false,
              fullscreenControl: true,
              streetViewControl: false,
            }}
          >
            {/* Add markers for each location */}
            {locations.map((location, index) => (
              <Marker
                key={`${location.lat}-${location.lng}-${location.timestamp}-${index}`}
                position={{ lat: location.lat, lng: location.lng }}
                label={{
                  text: `${index + 1}`,
                  color: "#fff",
                  fontWeight: "bold",
                }}
                title={`Location ${index + 1}: ${formatTimestamp(location.timestamp)}`}
              />
            ))}
          </GoogleMap>
        </div>
      )}
      {isLoaded && locations.length === 0 && (
        <div className="no-locations-message">
          No location data available for this agent.
        </div>
      )}
    </Modal>
  );
};

export default AgentLocationMapModal;
